import React, { useState, useEffect } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowLeft,
  Star,
  GitFork,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  ExternalLink,
  Download,
  Copy,
  Lock,
  Loader2,
  Bot,
  ClipboardCheck,
  Network,
  Link2,
  Award,
} from 'lucide-react';
import { githubApiService } from '../services/githubApi';
import { ProjectAnalysis } from '../types';
import SecurityAnalysis from './SecurityAnalysis';
import SecurityDashboard from './SecurityDashboard';
import SecurityKnowledgeGraph from './SecurityKnowledgeGraph';
import BadgeModal from './BadgeModal';
import HeuristicNotice from './HeuristicNotice';
import ComplianceStatusPill from './ComplianceStatusPill';
import { useAiVerification } from '../hooks/useAiVerification';

const AnalysisResult: React.FC = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // Support shareable URLs: /analyze?repo=owner/repo
  const { repoUrl: repoUrlFromState, githubToken } = (location.state as { repoUrl?: string; githubToken?: string }) || {};
  const repoUrl = repoUrlFromState || searchParams.get('repo') || undefined;
  const [activeTab, setActiveTab] = useState('summary');
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [showBadgeModal, setShowBadgeModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<ProjectAnalysis | null>(null);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('Starting analysis...');

  useEffect(() => {
    const analyzeRepository = async () => {
      if (!repoUrl) {
        setError('No repository URL provided');
        setLoading(false);
        return;
      }

      try {
        // Set token if provided
        if (githubToken) {
          githubApiService.setToken(githubToken);
        }

        // Set up progress callback
        githubApiService.setProgressCallback((progressValue, message) => {
          setProgress(progressValue);
          setProgressMessage(message);
        });

        // Perform analysis
        const result = await githubApiService.analyzeRepository(repoUrl);
        console.log('🔍 Full analysis result:', result);
        console.log('📊 Security analysis:', result.security);
        console.log('🔒 Code Security object:', result.security?.codeSecurity);
        setAnalysis(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred during analysis');
      } finally {
        setLoading(false);
      }
    };

    analyzeRepository();
  }, [location.state, repoUrl, githubToken]);

  const aiVerification = useAiVerification(
    repoUrl,
    githubToken,
    analysis?.security.vulnerabilities ?? []
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full mx-auto p-6">
          <div className="text-center">
            <div className="relative mb-6">
              <div className="w-16 h-16 mx-auto">
                <Loader2 className="w-16 h-16 text-blue-600 animate-spin" />
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-8 h-8 bg-charcoal-bg rounded-full"></div>
              </div>
            </div>
            
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Analyzing Repository</h2>
            <p className="text-gray-600 mb-6">{progressMessage}</p>
            
            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
              <div 
                className="bg-blue-600 h-3 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            
            <div className="text-sm text-gray-500">
              {progress}% complete
            </div>
            
            {/* Performance Tips */}
            <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="text-sm font-medium text-blue-900 mb-2">💡 Performance Tips</h3>
              <ul className="text-xs text-blue-800 space-y-1">
                <li>• Large repositories may take longer to analyze</li>
                <li>• Adding a GitHub token increases rate limits</li>
                <li>• Analysis is optimized for repositories up to 2000 files</li>
                <li>• Progress is saved and cached for faster subsequent runs</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !analysis) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center">
          <AlertTriangle className="h-16 w-16 text-sev-critical/80 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Analysis Failed</h2>
          <p className="text-gray-600 mb-6">{error || 'No analysis data available'}</p>
          <Link 
            to="/" 
            className="btn-primary inline-flex items-center px-4 py-2"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const { repository, security, aiComplianceInsights } = analysis;

  // Debug logging for folder structure
  if (analysis?.architecture?.folderStructure) {
    console.log('FolderStructureDiagram data:', analysis.architecture.folderStructure);
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copyShareLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2500);
  };

  const exportReport = () => {
    const report = {
      repository: repository.full_name,
      analysis: analysis,
      generatedAt: new Date().toISOString(),
    };

    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${repository.name}-analysis.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportSBOM = (format: 'json' | 'csv') => {
    const deps = analysis.architecture?.dependencies ?? {};
    const depVulns = security.vulnerabilities.filter(v => v.category === 'dependency');

    const components = Object.entries(deps).map(([name, version]) => {
      const cleanVersion = (version as string).replace(/[^0-9.]/g, '');
      const cves = depVulns
        .filter(v => v.title.startsWith(`${name}@`) || v.affectedFiles.includes('package.json'))
        .filter(v => v.title.includes(name))
        .map(v => ({ id: v.cve ?? v.id, severity: v.severity, summary: v.title }));
      return { name, version: cleanVersion || version, ecosystem: 'npm', vulnerabilities: cves, safe: cves.length === 0 };
    });

    if (format === 'json') {
      const sbom = {
        bomFormat: 'GitHubMate-SBOM',
        specVersion: '1.0',
        serialNumber: `urn:uuid:${Date.now()}`,
        version: 1,
        metadata: {
          timestamp: new Date().toISOString(),
          component: { type: 'library', name: repository.full_name },
          tools: [{ name: 'GitHubMate', version: '1.0' }],
        },
        components,
        summary: {
          total: components.length,
          vulnerable: components.filter(c => !c.safe).length,
          safe: components.filter(c => c.safe).length,
        },
      };
      const blob = new Blob([JSON.stringify(sbom, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${repository.name}-sbom.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } else {
      const header = 'Name,Version,Ecosystem,Safe,CVEs\n';
      const rows = components.map(c =>
        `"${c.name}","${c.version}","${c.ecosystem}","${c.safe ? 'Yes' : 'No'}","${c.vulnerabilities.map(v => v.id).join('; ')}"`
      ).join('\n');
      const blob = new Blob([header + rows], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${repository.name}-sbom.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const tabs = [
    { id: 'summary',        label: 'Summary',        icon: TrendingUp },
    { id: 'security',       label: 'Security',       icon: Lock },
    { id: 'attacksurface',  label: 'Attack Surface', icon: Network },
    { id: 'compliance',     label: 'Compliance',     icon: ClipboardCheck },
  ];

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-lime';
    if (score >= 60) return 'text-sev-medium';
    return 'text-sev-critical';
  };


  const repoName = analysis?.repository?.name ?? 'Repository';

  return (
    <>
    <Helmet>
      <title>{`${repoName} Security Scan — GitHubMate`}</title>
      <meta name="description" content={`Security scan results for ${repoName}: OWASP Top 10, LLM Top 10, secrets, CVEs, IaC misconfigurations, and vibe-code risk analysis.`} />
      <meta name="robots" content="noindex" />
    </Helmet>
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <Link to="/" className="btn-secondary">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Link>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={copyShareLink}
            className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg text-sm font-semibold border transition-colors ${
              linkCopied
                ? 'bg-lime/10 border-lime/40 text-lime'
                : 'bg-primary-50 border-primary-200 text-primary-700 hover:bg-primary-100'
            }`}
          >
            <Link2 className="h-4 w-4" />
            <span>{linkCopied ? 'Link copied!' : 'Share scan'}</span>
          </button>
          <button
            onClick={() => setShowBadgeModal(true)}
            className="btn-secondary flex items-center space-x-2"
            title="Get a README badge showing this repo's security score"
          >
            <Award className="h-4 w-4" />
            <span>README Badge</span>
          </button>
          <button
            onClick={exportReport}
            className="btn-secondary flex items-center space-x-2"
          >
            <Download className="h-4 w-4" />
            <span>Export Report</span>
          </button>
          <button
            onClick={() => exportSBOM('json')}
            className="btn-secondary flex items-center space-x-2"
            title="Software Bill of Materials — all dependencies with CVE status"
          >
            <Download className="h-4 w-4" />
            <span>SBOM JSON</span>
          </button>
          <button
            onClick={() => exportSBOM('csv')}
            className="btn-secondary flex items-center space-x-2"
            title="SBOM as CSV spreadsheet"
          >
            <Download className="h-4 w-4" />
            <span>SBOM CSV</span>
          </button>
          <button
            onClick={() => copyToClipboard(JSON.stringify(analysis, null, 2))}
            className="btn-secondary flex items-center space-x-2"
          >
            <Copy className="h-4 w-4" />
            <span>{copied ? 'Copied!' : 'Copy JSON'}</span>
          </button>
        </div>
      </div>

      {/* Repository Info */}
      <div className="card mb-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-4">
            <img
              src={repository.owner.avatar_url}
              alt={repository.owner.login}
              className="w-16 h-16 rounded-lg"
            />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                {repository.full_name}
              </h1>
              <p className="text-gray-600 mb-3 max-w-2xl">
                {repository.description || 'No description available'}
              </p>
              <div className="flex items-center space-x-4 text-sm text-gray-500">
                <div className="flex items-center space-x-1">
                  <Star className="h-4 w-4" />
                  <span>{repository.stargazers_count.toLocaleString()}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <GitFork className="h-4 w-4" />
                  <span>{repository.forks_count.toLocaleString()}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <AlertTriangle className="h-4 w-4" />
                  <span>{repository.open_issues_count.toLocaleString()}</span>
                </div>
                <span>•</span>
                <span>{repository.language}</span>
                <span>•</span>
                <span>Updated {new Date(repository.updated_at).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
          <a
            href={`https://github.com/${repository.full_name}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary flex items-center space-x-2"
          >
            <ExternalLink className="h-4 w-4" />
            <span>View on GitHub</span>
          </a>
        </div>
      </div>

      {/* Security Posture Banner */}
      {(() => {
        const score = security.overallScore;
        const r = 42; const cx = 60; const cy = 60;
        const circ = 2 * Math.PI * r;
        const filled = (score / 100) * circ;
        const arcColor = score >= 80 ? '#22c55e' : score >= 60 ? '#eab308' : score >= 40 ? '#f97316' : '#ef4444';
        const riskColors: Record<string, string> = {
          critical: 'bg-sev-critical/10 text-sev-critical border-sev-critical/40',
          high:     'bg-sev-high/10 text-sev-high border-sev-high/40',
          medium:   'bg-sev-medium/10 text-sev-medium border-sev-medium/40',
          low:      'bg-limedim text-lime border-lime/40',
        };
        const sev = [
          { label: 'Critical', bg: 'bg-sev-critical/10',    border: 'border-sev-critical/30',    text: 'text-sev-critical',    sub: 'text-sev-critical/80',    count: security.vulnerabilities.filter(v => v.severity === 'critical').length },
          { label: 'High',     bg: 'bg-sev-high/10', border: 'border-sev-high/30', text: 'text-sev-high', sub: 'text-sev-high/80', count: security.vulnerabilities.filter(v => v.severity === 'high').length },
          { label: 'Medium',   bg: 'bg-sev-medium/10', border: 'border-sev-medium/30', text: 'text-sev-medium', sub: 'text-sev-medium/80', count: security.vulnerabilities.filter(v => v.severity === 'medium').length },
          { label: 'Low',      bg: 'bg-blue-50',   border: 'border-blue-200',   text: 'text-blue-600',   sub: 'text-blue-500',   count: security.vulnerabilities.filter(v => v.severity === 'low').length },
        ];
        return (
          <div className="bg-charcoal-card border border-gray-200 rounded-xl p-5 mb-6 shadow-sm">
            <div className="flex flex-col md:flex-row md:items-center gap-5">

              {/* Donut gauge + score text */}
              <div className="flex items-center gap-4 flex-shrink-0">
                <svg width="120" height="120" viewBox="0 0 120 120">
                  <circle cx={cx} cy={cy} r={r} fill="none" stroke="#e5e7eb" strokeWidth={10} />
                  <circle cx={cx} cy={cy} r={r} fill="none" stroke={arcColor} strokeWidth={10}
                    strokeDasharray={`${filled} ${circ - filled}`} strokeLinecap="round"
                    transform={`rotate(-90 ${cx} ${cy})`} />
                  <text x={cx} y={cy - 6} textAnchor="middle" fontSize="18" fontWeight="bold" fill={arcColor}>{score}%</text>
                  <text x={cx} y={cy + 10} textAnchor="middle" fontSize="9" fill="#9ca3af">Security Score</text>
                </svg>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Risk Level</p>
                  <span className={`text-sm font-bold px-3 py-1 rounded-full border ${riskColors[security.riskLevel] ?? 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                    {security.riskLevel.toUpperCase()}
                  </span>
                  {security.avsvLevel && (
                    <p className={`mt-2 text-xs font-semibold ${getScoreColor(score)}`}>
                      ASVS Level {security.avsvLevel} Ready
                    </p>
                  )}
                  {!security.avsvLevel && (
                    <p className="mt-2 text-xs text-sev-critical/80 font-medium">ASVS: Needs Remediation</p>
                  )}
                </div>
              </div>

              {/* Vertical divider */}
              <div className="hidden md:block w-px self-stretch bg-gray-100" />

              {/* Severity grid */}
              <div className="grid grid-cols-4 gap-3 flex-1">
                {sev.map(s => (
                  <div key={s.label} className={`rounded-xl border ${s.bg} ${s.border} p-3 text-center`}>
                    <div className={`text-2xl font-bold ${s.text}`}>{s.count}</div>
                    <div className={`text-xs font-medium ${s.sub} mt-0.5`}>{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Vertical divider */}
              <div className="hidden md:block w-px self-stretch bg-gray-100" />

              {/* Vibe-code + total */}
              <div className="flex flex-col gap-2 flex-shrink-0 text-center">
                <div className="text-3xl font-bold text-gray-800">{security.vulnerabilities.length}</div>
                <div className="text-xs text-gray-400 uppercase tracking-wide">Total Findings</div>
                {security.vibeCodeAnalysis?.isVibeCodeProject && (
                  <span className="mt-1 text-xs bg-accent-amber/10 border border-accent-amber/40 text-accent-amber px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                    <Bot className="h-3 w-3" /> AI Project
                  </span>
                )}
              </div>

            </div>
          </div>
        );
      })()}

      {/* Tabs */}
      <div className="card">
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="min-h-[400px]">
          {activeTab === 'summary' && (
            <div className="space-y-8">
              {/* Wiz-style Security Dashboard */}
              <SecurityDashboard security={security} repoName={repository.full_name} aiVerification={aiVerification} />


            </div>
          )}


          {activeTab === 'security' && (
            <div className="space-y-6">
              <SecurityAnalysis security={security} />
            </div>
          )}

          {activeTab === 'attacksurface' && (
            <div className="space-y-4">
              {/* Intro callout */}
              <div className="flex items-start gap-3 rounded-xl border border-purple-900/40 bg-purple-950/20 px-4 py-3">
                <Network className="h-5 w-5 text-purple-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-purple-300">Security Knowledge Graph — Attack Surface Map</p>
                  <p className="text-xs text-purple-400/80 mt-0.5">
                    Explore how vulnerabilities, files, dependencies, secrets, and compliance requirements interconnect. Click any node to inspect it. Use filters to focus on what matters most.
                  </p>
                </div>
              </div>
              <SecurityKnowledgeGraph security={security} repoName={repository.full_name} />
            </div>
          )}

          {activeTab === 'compliance' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <ClipboardCheck className="h-5 w-5 text-blue-500 mr-2" />
                  Compliance Readiness
                </h3>
                <HeuristicNotice context="compliance" />
              </div>

              {/* ASVS Level Banner */}
              <div className={`rounded-lg p-4 border ${
                security.avsvLevel === 3 ? 'bg-limedim border-lime/40' :
                security.avsvLevel === 2 ? 'bg-blue-50 border-blue-300' :
                security.avsvLevel === 1 ? 'bg-sev-medium/10 border-sev-medium/40' :
                'bg-sev-critical/10 border-sev-critical/40'
              }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`font-semibold ${
                      security.avsvLevel === 3 ? 'text-green-800' :
                      security.avsvLevel === 2 ? 'text-blue-800' :
                      security.avsvLevel === 1 ? 'text-yellow-800' :
                      'text-red-800'
                    }`}>
                      OWASP ASVS {security.avsvLevel ? `Level ${security.avsvLevel}` : 'Not Compliant'}
                    </p>
                    <p className={`text-sm mt-0.5 ${
                      security.avsvLevel === 3 ? 'text-lime' :
                      security.avsvLevel === 2 ? 'text-blue-700' :
                      security.avsvLevel === 1 ? 'text-sev-medium' :
                      'text-sev-critical'
                    }`}>
                      {security.avsvLevel === 3 && 'Advanced — suitable for healthcare, fintech, high-value applications'}
                      {security.avsvLevel === 2 && 'Standard — suitable for most business applications handling sensitive data'}
                      {security.avsvLevel === 1 && 'Basic — minimum security baseline met'}
                      {!security.avsvLevel && 'Critical/high vulnerabilities must be resolved before any compliance level is met'}
                    </p>
                  </div>
                  <div className={`text-2xl font-bold ${
                    security.avsvLevel === 3 ? 'text-lime' :
                    security.avsvLevel === 2 ? 'text-blue-600' :
                    security.avsvLevel === 1 ? 'text-sev-medium' :
                    'text-sev-critical'
                  }`}>
                    {security.compliance.complianceScore}%
                  </div>
                </div>
              </div>

              {/* Compliance Standards Grid */}
              <div className="grid md:grid-cols-2 gap-6">
                {[
                  {
                    name: 'SOC 2',
                    description: 'Security, Availability & Confidentiality controls',
                    compliant: security.compliance.soxCompliant,
                    report: security.compliance.complianceReports.sox,
                    color: 'blue',
                  },
                  {
                    name: 'GDPR',
                    description: 'EU data protection & privacy regulation',
                    compliant: security.compliance.gdprCompliant,
                    report: security.compliance.complianceReports.gdpr,
                    color: 'teal',
                  },
                  {
                    name: 'HIPAA',
                    description: 'Healthcare data security requirements',
                    compliant: security.compliance.hipaaCompliant,
                    report: security.compliance.complianceReports.hipaa,
                    color: 'green',
                  },
                  {
                    name: 'PCI DSS',
                    description: 'Payment card data security standard',
                    compliant: security.compliance.pciCompliant,
                    report: security.compliance.complianceReports.pci,
                    color: 'orange',
                  },
                ].map((std) => (
                  <div key={std.name} className="bg-charcoal-card border border-gray-200 rounded-lg p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <span className="font-semibold text-gray-900">{std.name}</span>
                        <p className="text-xs text-gray-500 mt-0.5">{std.description}</p>
                      </div>
                      <div className="text-right">
                        <div className={`text-lg font-bold ${std.report?.score >= 80 ? 'text-lime' : std.report?.score >= 60 ? 'text-sev-medium' : 'text-sev-critical'}`}>
                          {std.report?.score ?? 0}%
                        </div>
                        <ComplianceStatusPill compliant={std.compliant} />
                      </div>
                    </div>

                    {/* Score bar */}
                    <div className="w-full bg-gray-100 rounded-full h-1.5 mb-3">
                      <div
                        className={`h-1.5 rounded-full ${std.report?.score >= 80 ? 'bg-limedim0' : std.report?.score >= 60 ? 'bg-sev-medium/100' : 'bg-sev-critical/100'}`}
                        style={{ width: `${std.report?.score ?? 0}%` }}
                      />
                    </div>

                    {/* Requirements checklist */}
                    {std.report?.requirements?.length > 0 && (
                      <ul className="space-y-1.5 mb-3">
                        {std.report.requirements.slice(0, 4).map((req, i) => (
                          <li key={i} className="flex items-start space-x-2 text-xs text-gray-700">
                            {req.compliant
                              ? <CheckCircle className="h-3.5 w-3.5 text-lime flex-shrink-0 mt-0.5" />
                              : <AlertTriangle className="h-3.5 w-3.5 text-sev-critical/80 flex-shrink-0 mt-0.5" />
                            }
                            <span className={req.compliant ? 'text-gray-600' : 'text-gray-800 font-medium'}>{req.requirement}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Issues */}
                    {std.report?.issues?.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-100">
                        <p className="text-xs font-medium text-sev-critical mb-1">Gaps to address:</p>
                        <ul className="space-y-0.5">
                          {std.report.issues.slice(0, 2).map((issue, i) => (
                            <li key={i} className="text-xs text-sev-critical flex items-start space-x-1">
                              <span className="flex-shrink-0">•</span>
                              <span>{issue}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Overall compliance issues */}
              {security.compliance.complianceIssues.length > 0 && (
                <div className="bg-charcoal-card border border-gray-200 rounded-lg p-5">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <AlertTriangle className="h-4 w-4 text-sev-critical/80 mr-2" />
                    Compliance Issues to Resolve
                  </h4>
                  <ul className="space-y-2">
                    {security.compliance.complianceIssues.map((issue, i) => (
                      <li key={i} className="flex items-start space-x-2 text-sm text-gray-700">
                        <div className="w-2 h-2 bg-sev-critical/100 rounded-full mt-2 flex-shrink-0" />
                        <span>{issue}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* AI Compliance Insights */}
              {aiComplianceInsights && (
                <div className="bg-charcoal-card2 border border-accent-teal/30 rounded-xl p-5 space-y-5">
                  <div className="flex items-center space-x-2 mb-1">
                    <div className="w-6 h-6 rounded-md bg-indigo-600 flex items-center justify-center">
                      <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                    </div>
                    <h4 className="font-semibold text-gray-900">AI Compliance Analysis</h4>
                    <span className="text-xs bg-accent-teal/15 text-accent-teal px-2 py-0.5 rounded-full font-medium">Powered by AI</span>
                  </div>

                  {/* Overall assessment */}
                  <p className="text-sm text-gray-900 leading-relaxed">{aiComplianceInsights.overallAssessment}</p>

                  {/* Per-framework AI cards */}
                  <div className="grid md:grid-cols-2 gap-4">
                    {([
                      { key: 'soc2', label: 'SOC 2', color: 'blue' },
                      { key: 'gdpr', label: 'GDPR', color: 'teal' },
                      { key: 'hipaa', label: 'HIPAA', color: 'green' },
                      { key: 'pci', label: 'PCI DSS', color: 'orange' },
                    ] as const).map(({ key, label }) => {
                      const fw = aiComplianceInsights[key];
                      const readinessColor = fw.readiness === 'Ready' ? 'text-lime bg-limedim' : fw.readiness === 'Gaps Found' ? 'text-sev-medium bg-sev-medium/10' : 'text-sev-critical bg-sev-critical/10';
                      return (
                        <div key={key} className="bg-charcoal-card border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <span className="font-semibold text-gray-900 text-sm">{label}</span>
                            <div className="flex items-center space-x-2">
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${readinessColor}`}>{fw.readiness}</span>
                              <span className="text-xs text-gray-400">{fw.timeline}</span>
                            </div>
                          </div>
                          {fw.topGaps.length > 0 && (
                            <div className="mb-2">
                              <p className="text-xs font-medium text-sev-critical mb-1">Top gaps:</p>
                              <ul className="space-y-0.5">
                                {fw.topGaps.map((gap, i) => (
                                  <li key={i} className="text-xs text-gray-700 flex items-start space-x-1">
                                    <span className="text-red-400 flex-shrink-0">•</span><span>{gap}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {fw.quickFixes.length > 0 && (
                            <div>
                              <p className="text-xs font-medium text-lime mb-1">Quick fixes:</p>
                              <ul className="space-y-0.5">
                                {fw.quickFixes.map((fix, i) => (
                                  <li key={i} className="text-xs text-gray-700 flex items-start space-x-1">
                                    <span className="text-lime flex-shrink-0">✓</span><span>{fix}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Priority actions + estimated effort */}
                  <div className="grid md:grid-cols-2 gap-4">
                    {aiComplianceInsights.priorityActions.length > 0 && (
                      <div className="bg-charcoal-card border border-gray-200 rounded-lg p-4">
                        <p className="text-xs font-semibold text-gray-900 mb-2">Priority Actions</p>
                        <ol className="space-y-1">
                          {aiComplianceInsights.priorityActions.map((action, i) => (
                            <li key={i} className="text-xs text-gray-700 flex items-start space-x-2">
                              <span className="flex-shrink-0 w-4 h-4 rounded-full bg-accent-teal/15 text-accent-teal text-center font-semibold leading-4">{i + 1}</span>
                              <span>{action}</span>
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}
                    {aiComplianceInsights.estimatedEffort && (
                      <div className="bg-charcoal-card border border-gray-200 rounded-lg p-4 flex flex-col justify-center">
                        <p className="text-xs font-semibold text-gray-900 mb-1">Estimated Effort</p>
                        <p className="text-sm text-accent-teal font-medium">{aiComplianceInsights.estimatedEffort}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}



        </div>
      </div>
    </div>

    {showBadgeModal && (
      <BadgeModal
        score={security.overallScore}
        riskLevel={security.riskLevel}
        repoFullName={repository.full_name}
        onClose={() => setShowBadgeModal(false)}
      />
    )}
    </>
  );
};

export default AnalysisResult; 